// src/pages/bookings/components/DeleteBookingsDialog.tsx

import { useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiX } from "react-icons/fi";

import type { AdminBulkDeleteSkipped } from "@/api/features/bookings/types";

export type DeleteTarget = {
  id: number;
  bookingCode: string;
  status: string;
};

/**
 * Confirmation for admin booking deletes.
 *
 * Deliberately hard to trigger by accident:
 * - a reason is always required (it lands in booking_logs and audit_logs)
 * - a single delete requires typing the booking code
 * - a bulk delete requires typing DELETE
 *
 * Bookings holding a captured payment are refused server-side, so the result
 * view reports what was skipped rather than pretending everything succeeded.
 */
export default function DeleteBookingsDialog({
  open,
  targets,
  isPending,
  skipped,
  onCancel,
  onConfirm,
  onDone,
}: {
  open: boolean;
  targets: DeleteTarget[];
  isPending: boolean;
  /** Set once a bulk delete returns with rows the server refused. */
  skipped: AdminBulkDeleteSkipped[] | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");

  const isBulk = targets.length > 1;
  const single = targets[0];

  const requiredPhrase = isBulk ? "DELETE" : single?.bookingCode ?? "";

  // Never carry a previous confirmation into the next dialog.
  useEffect(() => {
    if (open) {
      setReason("");
      setTyped("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isPending) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isPending, onCancel]);

  const reasonOk = reason.trim().length >= 3;
  const phraseOk =
    typed.trim().toUpperCase() === requiredPhrase.trim().toUpperCase() &&
    requiredPhrase.length > 0;
  const canSubmit = reasonOk && phraseOk && !isPending;

  const confirmedCount = useMemo(
    () => targets.length - (skipped?.length ?? 0),
    [targets.length, skipped]
  );

  if (!open) return null;

  const showingResult = skipped != null && skipped.length > 0;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={() => !isPending && onCancel()}
      />

      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">
              {showingResult
                ? "Some bookings were not deleted"
                : isBulk
                ? `Delete ${targets.length} bookings`
                : "Delete booking"}
            </h3>
            <button
              className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-40"
              onClick={showingResult ? onDone : onCancel}
              disabled={isPending}
              aria-label="Close"
            >
              <FiX />
            </button>
          </div>

          {showingResult ? (
            <div className="p-5">
              <p className="text-sm text-gray-700">
                {confirmedCount} deleted, {skipped!.length} skipped. Bookings
                with a captured payment cannot be deleted — cancel or refund
                them instead.
              </p>

              <ul className="mt-3 max-h-56 overflow-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {skipped!.map((s) => (
                  <li
                    key={s.bookingId}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                  >
                    <span className="font-medium text-gray-900">
                      #{s.bookingId}
                    </span>
                    <span className="text-gray-500">{s.reason}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={onDone}
                  className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4 p-5">
                <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <FiAlertTriangle
                    className="mt-0.5 shrink-0 text-amber-600"
                    aria-hidden
                  />
                  <p className="text-xs text-amber-800">
                    The {isBulk ? "bookings" : "booking"} will be removed from
                    all admin lists and counts. This is a soft delete — the
                    record is retained for audit and can be restored by an
                    engineer.
                  </p>
                </div>

                {isBulk ? (
                  <div className="max-h-32 overflow-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {targets.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs"
                      >
                        <span className="font-medium text-gray-900">
                          {t.bookingCode || `#${t.id}`}
                        </span>
                        <span className="text-gray-500">{t.status}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-700">
                    Deleting{" "}
                    <span className="font-semibold text-gray-900">
                      {single?.bookingCode || `#${single?.id}`}
                    </span>{" "}
                    ({single?.status}).
                  </p>
                )}

                <div>
                  <label
                    htmlFor="delete-reason"
                    className="mb-1 block text-xs font-medium text-gray-700"
                  >
                    Reason <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="delete-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Test data cleanup before launch"
                    maxLength={500}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Recorded in the booking log and the audit trail.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="delete-confirm"
                    className="mb-1 block text-xs font-medium text-gray-700"
                  >
                    Type{" "}
                    <span className="font-mono font-semibold text-gray-900">
                      {requiredPhrase}
                    </span>{" "}
                    to confirm
                  </label>
                  <input
                    id="delete-confirm"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    autoComplete="off"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
                <button
                  onClick={onCancel}
                  disabled={isPending}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={() => onConfirm(reason.trim())}
                  disabled={!canSubmit}
                  className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                >
                  {isPending
                    ? "Deleting…"
                    : isBulk
                    ? `Delete ${targets.length}`
                    : "Delete"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
